/**
 * Top-down "map" of the peers you hear, in listener space (you are at the center, facing up).
 * Shows only peers the central already sends (subscribed ones): no extra information (§7.4 T7).
 *
 * Minecraft-style, square shape: a square frame with pixel-cut corners, square distance rings
 * inside and a light block grid (no circles, no diagonals except the peers' names). Rings are
 * proportional to the distances in blocks (refDistance, hearRadius / 2, hearRadius); peers are always drawn with the head of their skin (mc-heads, same image as the player
 * cards), falling back to an original pixel "Steve-style" head.
 */
import { memo, useId, useState } from 'react';
import type { AudioMode, GainConfig } from '../api/protocol';
import { toListenerSpace } from '../spatial/math';
import type { PeerAudioState } from '../spatial/SpatialMixer';
import type { PeerView } from '../voice/VoiceController';
import { avatarSrc, PEER_AVATAR_SIZE } from './Avatar';
import { SteveRects } from './SteveHead';
import { teamColor } from './team';

/** Half side of the square frame (viewBox units): 15 grid cells of 8 units. */
export const FRAME = 120;
/** Size of the pixel step cut into each corner of the frame (one grid cell = 8). */
const NOTCH = 6;
/** Half side of the outer distance ring (hearRadius), aligned on the block grid (12 cells). */
export const RING_EDGE = 96;
/** Peers farther than this square are pinned on its border (inside the frame, heads included). */
export const PEER_EDGE = FRAME - 12;
const HEAD = 18;
/** Grid cell (a "block" of the map). */
const CELL = 8;

/**
 * Square frame with a one-step "pixel" cut on each corner (only horizontal and vertical segments),
 * like a Minecraft map or inventory slot.
 */
const F = FRAME;
const N = NOTCH;
export const FRAME_PATH = [
  `M${-F + N} ${-F}`,
  `H${F - N}V${-F + N}H${F}`,
  `V${F - N}H${F - N}V${F}`,
  `H${-F + N}V${F - N}H${-F}`,
  `V${-F + N}H${-F + N}Z`,
].join('');

/** Distance from the center to the border of a square of half side `a`, along the direction (dx, dy). */
export function squareReach(dx: number, dy: number, a: number): number {
  const len = Math.hypot(dx, dy);
  const m = Math.max(Math.abs(dx), Math.abs(dy));
  if (m === 0) return a;
  return (a * len) / m;
}

/** Point on the border of the square of half side `a`, in the direction (dx, dy). */
function onBorder(dx: number, dy: number, a: number = FRAME): [number, number] {
  const m = Math.max(Math.abs(dx), Math.abs(dy));
  if (m === 0) return [0, -a];
  return [(dx / m) * a, (dy / m) * a];
}

/** View cone: a stepped ("pixel") triangle from you to the top edge, about 37 degrees wide. */
const CONE_HALF = 0.34;
function stairCone(): string {
  // left edge, from you up to the top edge, in steps of one grid cell; the right edge mirrors it
  const left: [number, number][] = [[0, 0]];
  let x = 0;
  for (let y = -CELL; y >= -F; y -= CELL) {
    left.push([x, y]);
    x = -CONE_HALF * -y;
    left.push([x, y]);
  }
  const right = [...left].reverse().map(([px, py]) => [-px, py] as [number, number]);
  const pts = [...left, ...right.slice(0, -1)];
  return `M${pts.map(([px, py]) => `${(px || 0).toFixed(1)} ${py}`).join('L')}Z`;
}
const CONE_PATH = stairCone();

/** Skin URLs that failed to load in this tab: shown as the fallback head without retrying. */
const failedSkins = new Set<string>();

const RadarHead = memo(function RadarHead({ peer }: { peer: PeerView }) {
  const url = avatarSrc(peer.identity, peer.name, PEER_AVATAR_SIZE);
  const [, setFailed] = useState(0);
  const half = HEAD / 2;
  if (!url || failedSkins.has(url)) {
    return (
      <svg x={-half} y={-half} width={HEAD} height={HEAD} viewBox="0 0 8 8" shapeRendering="crispEdges" data-testid="radar-head" data-source="steve">
        <SteveRects />
      </svg>
    );
  }
  return (
    <image
      href={url}
      x={-half}
      y={-half}
      width={HEAD}
      height={HEAD}
      preserveAspectRatio="none"
      className="radar-head-img"
      data-testid="radar-head"
      data-source="skin"
      onError={() => {
        failedSkins.add(url);
        setFailed((n) => n + 1);
      }}
    />
  );
});

export function Radar({
  peers,
  audio,
  yaw,
  gain,
  mode,
  highlight = null,
}: {
  peers: PeerView[];
  audio: Map<string, PeerAudioState>;
  yaw: number;
  gain: GainConfig;
  mode: AudioMode;
  /** Identity to emphasize (hovered player card). */
  highlight?: string | null;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const scale = RING_EDGE / gain.hearRadius;
  const north = toListenerSpace({ x: 0, y: 0, z: -1 }, yaw);
  const [nx, ny] = onBorder(north.x, north.z);
  const rings = [gain.refDistance, gain.hearRadius / 2, gain.hearRadius];

  // Paint order = stacking order in SVG: quiet peers first, then who speaks, the hovered one on top.
  const rank = (p: PeerView) => (highlight === p.identity ? 2 : p.speaking ? 1 : 0);
  const ordered = mode === 'spatial' ? [...peers].sort((a, b) => rank(a) - rank(b)) : [];

  return (
    <figure className="radar" aria-label="Mappa dei giocatori che senti" data-testid="radar">
      <svg viewBox="-128 -128 256 256" role="img">
        <title>Posizione dei giocatori rispetto a te: in alto è la direzione in cui guardi</title>
        <defs>
          <clipPath id={`radar-clip-${uid}`}>
            <path d={FRAME_PATH} />
          </clipPath>
          <pattern id={`radar-grid-${uid}`} width={CELL} height={CELL} patternUnits="userSpaceOnUse" x={0} y={0}>
            <path d={`M${CELL} 0H0V${CELL}`} className="radar-grid-line" />
          </pattern>
        </defs>
        <path d={FRAME_PATH} className="radar-bg" />
        <g clipPath={`url(#radar-clip-${uid})`}>
          <rect x={-F} y={-F} width={F * 2} height={F * 2} fill={`url(#radar-grid-${uid})`} />
          {/* view cone, up to the top edge */}
          <path d={CONE_PATH} className="radar-cone" />
        </g>
        <line x1={-F} x2={F} y1={0} y2={0} className="radar-axis" />
        <line x1={0} x2={0} y1={-F} y2={F} className="radar-axis" />
        {rings.map((b, i) => {
          const h = b * scale;
          return (
            <g key={b}>
              <rect x={-h} y={-h} width={h * 2} height={h * 2} className={i === rings.length - 1 ? 'radar-ring radar-ring-edge' : 'radar-ring'} data-testid="radar-ring" />
              <text x={h + 2.5} y={-h + 7} className="radar-ring-label">
                {Math.round(b)}
              </text>
            </g>
          );
        })}
        <path d={FRAME_PATH} className="radar-frame" data-testid="radar-frame" />
        <g className="radar-north" style={{ transform: `translate(${nx}px, ${ny}px)` }}>
          <rect x={-7} y={-7} width={14} height={14} />
          <text y={4}>N</text>
        </g>
        {/* you */}
        <path d="M0 -9 L6 6 L0 3 L-6 6 Z" className="radar-me" />

        {ordered.map((p) => {
          const a = audio.get(p.identity);
          if (!a || a.distance === null) return null;
          let x = a.position.x * scale;
          let y = a.position.z * scale;
          const len = Math.hypot(x, y);
          const reach = squareReach(x, y, PEER_EDGE);
          const out = len > reach;
          if (out) {
            x = (x / len) * reach;
            y = (y / len) * reach;
          }
          const color = teamColor(p.teamId);
          const hl = highlight === p.identity;
          return (
            <g
              key={p.identity}
              className={`radar-peer${p.speaking ? ' is-speaking' : ''}${out ? ' is-out' : ''}${hl ? ' is-highlight' : ''}`}
              data-identity={p.identity}
              data-testid="radar-peer"
              style={{ transform: `translate(${x}px, ${y}px)` }}
            >
              {p.speaking ? <rect x={-HEAD / 2 - 3} y={-HEAD / 2 - 3} width={HEAD + 6} height={HEAD + 6} className="radar-peer-ring" /> : null}
              <rect
                x={-HEAD / 2 - 1.5}
                y={-HEAD / 2 - 1.5}
                width={HEAD + 3}
                height={HEAD + 3}
                className="radar-peer-frame"
                style={color && !p.speaking && !hl ? { stroke: color } : undefined}
              />
              <RadarHead peer={p} />
              {/* name on the side facing the center, so it stays inside the compass */}
              <text x={x > 24 ? -HEAD / 2 - 4 : HEAD / 2 + 4} y={3} textAnchor={x > 24 ? 'end' : 'start'} className="radar-peer-name">
                {p.name}
              </text>
            </g>
          );
        })}
      </svg>
      {mode === 'global' ? (
        <figcaption className="radar-caption">Canale aperto: in questa fase tutti si sentono senza distanza.</figcaption>
      ) : (
        <figcaption className="radar-caption">Gli anelli indicano la distanza in blocchi</figcaption>
      )}
    </figure>
  );
}
